/* eslint-disable react/prop-types */
/**
 * RemarksTab — append-only remarks attached to any of 6 parent types.
 *
 * UX: a single Radio.Group selects one of [deal, lease_deed,
 * lease_deed_tract, well, well_tract, payment]. A secondary AntD Select
 * picks the specific entity for that parent type (auto-populated to
 * "Current Deal" when parent_table='deal'). The active (parent_table,
 * parent_id) feeds the grid + the create body.
 *
 * Append-only: backend exposes POST + DELETE but NOT PUT. We pass
 * `appendOnly` to `WigMetadataCrudPanel` to suppress the edit modal.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Radio,
  Select,
  Space,
  Typography,
  notification,
} from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";

const { Text } = Typography;

// parent_table values supported by the backend polymorphic validator for
// remarks. Order = display order in the radio.
const PARENT_OPTIONS = [
  { value: "deal", label: "Deal" },
  { value: "lease_deed", label: "Lease/Deed" },
  { value: "lease_deed_tract", label: "Lease Tract" },
  { value: "well", label: "Well" },
  { value: "well_tract", label: "Well Tract" },
  { value: "payment", label: "Payment" },
];

const HIDDEN_FIELDS = new Set(["parent_table", "parent_id"]);

const leaseDeedLabel = (ld) => {
  if (!ld) return "";
  const num = ld.external_number || `#${ld.id}`;
  const grantor = ld.lessor_grantor ? ` — ${ld.lessor_grantor}` : "";
  return `${num}${grantor}`;
};
const wellLabel = (w) => {
  if (!w) return "";
  const name = w.well_name || `Well #${w.id}`;
  const api = w.api_10 ? ` — API ${w.api_10}` : "";
  return `${name}${api}`;
};
const tractLabel = (t) => {
  if (!t) return "";
  const loc = t.location_type ? `${t.location_type} ` : "";
  return `${loc}Tract #${t.id}`;
};
const paymentLabel = (p) => {
  if (!p) return "";
  const t = p.payment_type || "Payment";
  const amt = p.amount != null ? ` — $${p.amount}` : "";
  return `${t}${amt} #${p.id}`;
};

const RemarksTab = ({ dealId, dealKey }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  // Scope state
  const [parentTable, setParentTable] = useState("deal");
  const [parentEntities, setParentEntities] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(dealId);
  const [scopeLoading, setScopeLoading] = useState(false);

  // Grid state
  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Load remarks metadata once.
  useEffect(() => {
    const load = async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const metaResp = await dealsService.getMetadata({
          token: tk,
          userOid,
          tableName: "remarks",
        });
        setMetadata(metaResp?.data?.data || []);
      } catch (err) {
        console.error("RemarksTab metadata load failed:", err);
        setErrMsg(apiErrorText(err));
      }
    };
    load();
  }, [getAuth]);

  // Load the parent-entity list for the currently selected parent_table.
  // For `deal` we just present the current deal; for the others we fetch
  // the deal-visible entities of that type.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!dealId) return;
      setScopeLoading(true);
      try {
        const { token: tk, userOid } = await getAuth();
        let entities = [];

        if (parentTable === "deal") {
          entities = [{ id: dealId, label: `Current Deal (${dealKey || dealId})` }];
        } else if (parentTable === "lease_deed") {
          const resp = await dealsService.listLeaseDeeds({
            token: tk,
            userOid,
            dealId,
          });
          entities = (resp?.data?.data || []).map((ld) => ({
            id: ld.id,
            label: leaseDeedLabel(ld),
          }));
        } else if (parentTable === "lease_deed_tract") {
          // Fan out: for each lease_deed on the deal, fetch its tracts.
          const ldResp = await dealsService.listLeaseDeeds({
            token: tk,
            userOid,
            dealId,
          });
          const lds = ldResp?.data?.data || [];
          const tractLists = await Promise.all(
            lds.map((ld) =>
              dealsService
                .listLeaseDeedTracts({
                  token: tk,
                  userOid,
                  leaseDeedId: ld.id,
                })
                .then((r) => (r?.data?.data || []).map((t) => ({ ld, t })))
                .catch(() => [])
            )
          );
          entities = tractLists.flat().map(({ ld, t }) => ({
            id: t.id,
            label: `${leaseDeedLabel(ld)} / Tract #${t.id}`,
          }));
        } else if (parentTable === "well") {
          const resp = await dealsService.listWells({
            token: tk,
            userOid,
            dealId,
          });
          entities = (resp?.data?.data || []).map((w) => ({
            id: w.id,
            label: wellLabel(w),
          }));
        } else if (parentTable === "well_tract") {
          const wResp = await dealsService.listWells({
            token: tk,
            userOid,
            dealId,
          });
          const ws = wResp?.data?.data || [];
          const tractLists = await Promise.all(
            ws.map((w) =>
              dealsService
                .listWellTracts({ token: tk, userOid, wellId: w.id })
                .then((r) => (r?.data?.data || []).map((t) => ({ w, t })))
                .catch(() => [])
            )
          );
          entities = tractLists.flat().map(({ w, t }) => ({
            id: t.id,
            label: `${wellLabel(w)} / ${tractLabel(t)}`,
          }));
        } else if (parentTable === "payment") {
          // Deal-scoped payments only (the common case). Well-tract-scoped
          // payments aren't fanned out here — users add remarks to those
          // by navigating to the Payments tab → Well Tract scope.
          const resp = await dealsService.listPayments({
            token: tk,
            userOid,
            parentTable: "deal",
            parentId: dealId,
          });
          entities = (resp?.data?.data || []).map((p) => ({
            id: p.id,
            label: paymentLabel(p),
          }));
        }

        if (cancelled) return;
        setParentEntities(entities);
        // Auto-select first entity for the new scope.
        setSelectedParentId((prev) => {
          if (parentTable === "deal") return dealId;
          if (prev && entities.some((e) => e.id === prev)) return prev;
          return entities[0]?.id ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        console.error("RemarksTab scope load failed:", err);
        setErrMsg(apiErrorText(err));
      } finally {
        if (!cancelled) setScopeLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [parentTable, dealId, dealKey, getAuth]);

  // Load remarks for the active (parent_table, parent_id).
  const refresh = useCallback(async () => {
    if (!selectedParentId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listRemarks({
        token: tk,
        userOid,
        parentTable,
        parentId: selectedParentId,
      });
      setRows(resp?.data?.data || []);
    } catch (err) {
      console.error("RemarksTab remarks load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [parentTable, selectedParentId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sortedRows = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) =>
      String(b.created_at || "").localeCompare(String(a.created_at || ""))
    );
    return out;
  }, [rows]);

  const filteredMetadata = useMemo(
    () => (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field)),
    [metadata]
  );

  const surfaceParentError = (err) => {
    const msg = apiErrorText(err, { fallback: "Operation failed" });
    notification.error({ message: "Remark save failed", description: msg });
  };

  const onCreate = async (body) => {
    try {
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.createRemark({
        token: tk,
        userOid,
        body: {
          ...body,
          parent_table: parentTable,
          parent_id: selectedParentId,
        },
      });
      await refresh();
      return resp;
    } catch (err) {
      surfaceParentError(err);
      throw err;
    }
  };

  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteRemark({ token: tk, userOid, id });
    await refresh();
  };

  const parentOptions = useMemo(
    () => parentEntities.map((e) => ({ value: e.id, label: e.label })),
    [parentEntities]
  );

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load remarks"
          description={errMsg}
        />
      )}

      <Card size="small" title="Scope">
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space align="center" wrap>
            <Text strong>Parent type:</Text>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={parentTable}
              onChange={(e) => setParentTable(e.target.value)}
              options={PARENT_OPTIONS}
            />
          </Space>
          <Space align="center" wrap>
            <Text>Entity:</Text>
            <Select
              value={selectedParentId}
              options={parentOptions}
              onChange={setSelectedParentId}
              placeholder={
                parentTable === "deal"
                  ? "Current deal"
                  : `Pick a ${parentTable.replace(/_/g, " ")}`
              }
              style={{ minWidth: 360 }}
              loading={scopeLoading}
              disabled={parentTable === "deal" || !parentOptions.length}
              allowClear={parentTable !== "deal"}
              showSearch
              optionFilterProp="label"
            />
          </Space>
        </Space>
      </Card>

      <Card size="small" title={`Remarks (${sortedRows.length}) — newest first`}>
        {!selectedParentId ? (
          <Text type="secondary">
            Select an entity above to view its remarks.
          </Text>
        ) : (
          <WigMetadataCrudPanel
            title="Remarks"
            rows={sortedRows}
            metadata={filteredMetadata}
            loading={loading}
            allowView
            disableNonEditable
            onCreate={onCreate}
            onDelete={onDelete}
            appendOnly
            newRowDefaults={{
              parent_table: parentTable,
              parent_id: selectedParentId,
            }}
            emptyText="No remarks for this scope yet"
            height={420}
          />
        )}
      </Card>
    </Space>
  );
};

export default RemarksTab;
